import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Lock, CheckCircle, AlertTriangle, ArrowLeft } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import seacureLogo from '@/assets/SEACURE(CYBER SECURITY).png'

export const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { success, error: showError } = useToast()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [resetComplete, setResetComplete] = useState(false)

  const token = searchParams.get('token')
  const validSession: boolean | null = token !== null ? !!token : false

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (password !== confirmPassword) {
      showError('Passwords Mismatch', 'Passwords do not match')
      return
    }

    if (password.length < 8) {
      showError('Password Too Short', 'Password must be at least 8 characters')
      return
    }

    setLoading(true)
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error ?? 'Failed to reset password')

      setResetComplete(true)
      success(t('notifications.success'), 'Password updated successfully!')

      // Redirect to login after 2 seconds; ?reset=success triggers the
      // login page's post-reset banner.
      setTimeout(() => {
        navigate('/login?reset=success')
      }, 2000)
    } catch (err: any) {
      console.error('Error resetting password:', err)
      showError(t('errors.operationFailed'), err.message || 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  // Show loading while checking session
  if (validSession === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Verifying reset link...</p>
        </div>
      </div>
    )
  }

  // Show error if invalid/expired link
  if (validSession === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="text-center py-12">
            <AlertTriangle className="h-16 w-16 text-amber-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Invalid or Expired Link</h2>
            <p className="text-slate-600 mb-6">
              This password reset link is invalid or has expired.
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full"
              >
                Request New Reset Link
              </Button>
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
                variant="outline"
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <div className="mb-4">
          <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={seacureLogo} alt="Seacure" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">SeacureDB</h1>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-center flex items-center justify-center gap-2">
              <Lock className="h-5 w-5" />
              {resetComplete ? 'Password Updated!' : 'Set New Password'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {resetComplete ? (
              <div className="text-center space-y-4">
                <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
                <div>
                  <p className="text-slate-700 dark:text-slate-300 mb-2">
                    Your password has been successfully updated.
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Redirecting to login page...
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  Please enter your new password below.
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    New Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Must be at least 8 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Confirm New Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>

                {password && confirmPassword && password !== confirmPassword && (
                  <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                    Passwords do not match
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || password !== confirmPassword || !password}
                >
                  {loading ? 'Updating Password...' : 'Update Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-6 text-sm text-slate-600 dark:text-slate-400">
          <p>Protected by enterprise-grade security</p>
        </div>
      </div>
    </div>
  )
}

